import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../config/api';
import axios from 'axios';
import KycLegalTypePicker from './KycLegalTypePicker';

const KYCSection = ({ agentId, onUpdate }) => {
  const [idNumber, setIdNumber] = useState('');
  const [fullNames, setFullNames] = useState('');
  const [kraPin, setKraPin] = useState('');
  const [kycStatus, setKycStatus] = useState('pending');
  const [kycReviewNotes, setKycReviewNotes] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requiresManualKraPin, setRequiresManualKraPin] = useState(false);
  const [selfRegistered, setSelfRegistered] = useState(false);
  const [kycLegalType, setKycLegalType] = useState('');
  const [kycBusinessName, setKycBusinessName] = useState('');
  const [savingEntity, setSavingEntity] = useState(false);
  const [agencyName, setAgencyName] = useState('');

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchKYCData();
  }, [agentId]);

  // Auto-validate ID number when it changes (with debounce)
  useEffect(() => {
    if (!idNumber || idNumber.length < 8) {
      return;
    }

    // Debounce: wait 800ms after user stops typing
    const timeoutId = setTimeout(() => {
      if (idNumber && idNumber.length >= 8 && !validating && !fullNames) {
        validateIDNumber();
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [idNumber]);

  const fetchKYCData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agentToken');
      const response = await axios.get(`${API_BASE_URL}/api/agent/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data;
        setIdNumber(data.idNumber || '');
        setFullNames(data.fullNames || '');
        setKraPin(data.kraPin || '');
        setKycStatus(data.kycStatus || 'pending');
        setKycReviewNotes(data.kycReviewNotes || '');
        setDocuments(data.kycDocuments || []);
        setSelfRegistered(!!data.selfRegistered);
        setKycLegalType(data.kycLegalType || '');
        setKycBusinessName(data.kycBusinessName || data.agencyName || '');
        setAgencyName(data.agencyName || '');
      }
    } catch (error) {
      console.error('Error fetching KYC data:', error);
      setError('Failed to load KYC data');
    } finally {
      setLoading(false);
    }
  };

  const validateIDNumber = async () => {
    if (!idNumber || idNumber.length < 8) {
      setError('Please enter a valid ID number (at least 8 characters)');
      return;
    }

    try {
      setValidating(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('agentToken');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/agent/kyc/validate-id`,
        { idNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setFullNames(response.data.fullName || response.data.name || '');
        if (response.data.kraPin) {
          setKraPin(response.data.kraPin);
          setRequiresManualKraPin(false);
        } else {
          setRequiresManualKraPin(response.data.requiresManualKraPin || false);
        }
        setSuccess(`ID validated successfully`);
        fetchKYCData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error validating ID:', error);
      const payload = error.response?.data || {};
      let errMsg = payload.error || payload.message || 'Failed to validate ID number';
      if (payload.isTimeout || payload.errorCode === 'VALIDATION_TIMEOUT' || /timeout/i.test(errMsg)) {
        errMsg = 'Validation timed out. You can enter your details manually to continue.';
      } else if (/iPage/i.test(errMsg)) {
        errMsg = `${errMsg.trim()}${errMsg.trim().endsWith('.') ? ' ' : '. '}Contact KRA for assistance.`;
      }
      setError(errMsg);
    } finally {
      setValidating(false);
    }
  };

  const handleSaveEntityDetails = async () => {
    if (!kycLegalType) {
      setError('Select whether you are a sole proprietor or a business entity.');
      return;
    }
    if (kycLegalType === 'business_entity' && !String(kycBusinessName || '').trim()) {
      setError('Enter the name of the business as it appears on your certificate.');
      return;
    }
    try {
      setSavingEntity(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('agentToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/agent/kyc/entity-details`,
        {
          kycLegalType,
          kycBusinessName: kycLegalType === 'business_entity' ? kycBusinessName.trim() : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess(response.data.message || 'KYC details saved');
        fetchKYCData();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save KYC details');
    } finally {
      setSavingEntity(false);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only PDF, JPEG, and PNG files are allowed.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading((prev) => ({ ...prev, [documentType]: true }));
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('agentToken');
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType);

      const response = await axios.post(
        `${API_BASE_URL}/api/agent/kyc/upload-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setSuccess(`${documentType.replace(/_/g, ' ')} saved successfully`);
        setSelectedFiles((prev) => {
          const next = { ...prev };
          delete next[documentType];
          return next;
        });
        fetchKYCData();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(error.response?.data?.error || 'Failed to save document');
    } finally {
      setUploading((prev) => ({ ...prev, [documentType]: false }));
    }
  };

  const handleSaveAllDocuments = async () => {
    const pending = Object.entries(selectedFiles).filter(([, file]) => file);
    if (!pending.length) {
      setError('Attach the certificate files first, then click Save documents.');
      return;
    }
    for (const [documentType, file] of pending) {
      await handleFileUpload(documentType, file);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#f39c12', bg: '#fff3cd', text: 'Pending Review' },
      pending_review: { color: '#3498db', bg: '#d1ecf1', text: 'Pending Review' },
      approved: { color: '#27ae60', bg: '#d4edda', text: 'Approved' },
      rejected: { color: '#e74c3c', bg: '#f8d7da', text: 'Rejected' },
      flagged: { color: '#e67e22', bg: '#ffeaa7', text: 'Flagged' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        background: config.bg,
        color: config.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}>
        {config.text}
      </span>
    );
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    if (!doc) return { status: 'missing', doc: null };
    return { status: doc.review_status || 'pending', doc };
  };

  const handleViewDocument = async (docId, documentName) => {
    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/agent/kyc/documents/${docId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob' // Important: tell axios to expect binary data
        }
      );

      // Create a blob URL from the response
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it to open/download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = documentName || 'document';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error viewing document:', error);
      setError(error.response?.data?.error || 'Failed to view document. Please try again.');
    }
  };

  const handleDeleteDocument = async (docId, documentType) => {
    if (!window.confirm(`Are you sure you want to delete this ${documentType}? You can upload a new one after deletion.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('agentToken');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      await axios.delete(
        `${API_BASE_URL}/api/agent/kyc/documents/${docId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess(`${documentType} deleted successfully`);
      fetchKYCData(); // Refresh the document list
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error.response?.data?.error || 'Failed to delete document. Please try again.');
    }
  };

  const documentTypes = [
    { key: 'ira_licence', label: 'IRA Licence Document', icon: '📜' },
    ...(documents.some((doc) => doc.document_type === 'certificate_of_incorporation')
      ? [{ key: 'certificate_of_incorporation', label: 'Certificate of Incorporation or business certificate (already uploaded)', icon: '🏢' }]
      : []),
    ...(documents.some((doc) => doc.document_type === 'certificate_of_proficiency')
      ? [{ key: 'certificate_of_proficiency', label: 'Certificate of Proficiency (already uploaded)', icon: '🎓' }]
      : [])
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner-large"></div>
        <p>Loading KYC information...</p>
      </div>
    );
  }

  return (
    <div className="kyc-section" style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '12px',
      marginBottom: '2rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{ marginBottom: '1.5rem', color: '#2c3e50', fontSize: '1.5rem' }}>
        🔐 KYC Verification
      </h2>

      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #fcc'
        }}>
          ⚠️ {error}
        </div>
      )}

      {success && (
        <div style={{
          background: '#efe',
          color: '#3c3',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #cfc'
        }}>
          ✅ {success}
        </div>
      )}

      {selfRegistered && (
        <div style={{
          background: '#f8faf9',
          border: '1px solid #d7e3dc',
          borderRadius: '10px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ marginTop: 0, color: '#1A4736' }}>Document checklist</h3>
          <p style={{ marginTop: 0, color: '#475569' }}>
            Upload your IRA Licence. Credit Control can review after it is uploaded. Extra files already on this account are kept and are not required.
          </p>
          <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#334155' }}>
            {documentTypes.filter((docType) => docType.key !== 'certificate_of_proficiency').map((docType) => {
              const uploaded = documents.some((doc) => doc.document_type === docType.key);
              const isIra = docType.key === 'ira_licence';
              return (
                <li key={docType.key} style={{ marginBottom: '0.35rem' }}>
                  {uploaded ? 'Done — ' : (isIra ? 'Please upload — ' : 'On file — ')}{docType.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Overall KYC Status */}
      <div style={{
        background: 'linear-gradient(135deg, #1A4736 0%, #225741 100%)',
        padding: '1.5rem',
        borderRadius: '10px',
        marginBottom: '2rem',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>KYC Status</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {getStatusBadge(kycStatus)}
            </div>
          </div>
          {kycReviewNotes && (
            <div style={{ fontSize: '0.9rem', opacity: 0.9, maxWidth: '400px' }}>
              <strong>Review Notes:</strong> {kycReviewNotes}
            </div>
          )}
        </div>
      </div>

      {/* ID Number Validation Section */}
      <div style={{
        background: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '10px',
        marginBottom: '2rem',
        border: '2px solid #e9ecef'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>
          🆔 ID Number & Tax PIN Validation
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: '#7f8c8d' }}>
              ID Number *
            </label>
            <input
              type="text"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              placeholder="Enter your ID number"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
              disabled={false}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: '#7f8c8d' }}>
              Full Names
            </label>
            <input
              type="text"
              value={fullNames}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                background: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
              placeholder="Will be filled automatically after validation"
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: '#7f8c8d' }}>
              Tax PIN
            </label>
            <input
              type="text"
              value={kraPin}
              readOnly
              disabled
              placeholder="Will be filled automatically after validation"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                background: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
          </div>
        </div>

        {validating && (
          <div style={{
            background: '#e3f2fd',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            color: '#1976d2',
            fontSize: '0.9rem'
          }}>
            Validating...
          </div>
        )}

        {idNumber && !fullNames && !validating && (
          <button
            onClick={validateIDNumber}
            disabled={validating || !idNumber || idNumber.length < 8}
            style={{
              background: validating || !idNumber || idNumber.length < 8 ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '8px',
              cursor: validating || !idNumber || idNumber.length < 8 ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              marginTop: '1rem'
            }}
          >
            {validating ? 'Validating...' : '✅ Validate and Save'}
          </button>
        )}

        {idNumber && fullNames && (
          <div style={{
            background: '#d4edda',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            <strong>✅ ID Validated:</strong> {fullNames} {kraPin && `(Tax PIN: ${kraPin})`}
          </div>
        )}
      </div>

      <KycLegalTypePicker
        legalType={kycLegalType}
        businessName={kycBusinessName}
        onLegalTypeChange={(value) => {
          setKycLegalType(value);
          if (value === 'business_entity' && !kycBusinessName && agencyName) {
            setKycBusinessName(agencyName);
          }
        }}
        onBusinessNameChange={setKycBusinessName}
        showSave
        onSave={handleSaveEntityDetails}
        saving={savingEntity}
      />
      <div>
        <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
          📄 Required Documents
        </h3>
        
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {documentTypes.map((docType) => {
            const docStatus = getDocumentStatus(docType.key);
            const isUploading = uploading[docType.key];
            
            return (
              <div
                key={docType.key}
                style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '10px',
                  padding: '1.5rem',
                  background: docStatus.status === 'approved' ? '#f0fff0' : docStatus.status === 'rejected' ? '#fff0f0' : '#f8f9fa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                      {docType.icon} {docType.label}
                    </div>
                    {docStatus.doc && (
                      <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        Status: {getStatusBadge(docStatus.status)}
                        {docStatus.doc.review_notes && (
                          <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                            Notes: {docStatus.doc.review_notes}
                          </div>
                        )}
                        {docStatus.doc.is_flagged && docStatus.doc.flag_reason && (
                          <div style={{ marginTop: '0.5rem', color: '#e67e22', fontWeight: 'bold' }}>
                            ⚠️ Flagged: {docStatus.doc.flag_reason}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {docStatus.doc && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleViewDocument(docStatus.doc.id, docStatus.doc.document_name)}
                        style={{
                          background: '#3498db',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        👁️ View Document
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(docStatus.doc.id, docType.label)}
                        style={{
                          background: '#e74c3c',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setSelectedFiles((prev) => ({ ...prev, [docType.key]: file }));
                  }}
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                  }}
                />

                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleFileUpload(docType.key, selectedFiles[docType.key])}
                    disabled={isUploading || !selectedFiles[docType.key]}
                    style={{
                      background: (isUploading || !selectedFiles[docType.key]) ? '#95a5a6' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      padding: '0.6rem 1.25rem',
                      borderRadius: '6px',
                      cursor: (isUploading || !selectedFiles[docType.key]) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.95rem'
                    }}
                    title={!selectedFiles[docType.key] ? 'Attach a file first' : 'Save this document'}
                  >
                    {isUploading ? 'Saving...' : 'Save this document'}
                  </button>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {selectedFiles[docType.key] ? `Selected: ${selectedFiles[docType.key].name}` : 'No file selected'}
                  </div>
                </div>
                
                {docStatus.doc && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    Uploaded: {new Date(docStatus.doc.created_at).toLocaleDateString()}
                    {docStatus.doc.reviewed_at && (
                      <span> | Reviewed: {new Date(docStatus.doc.reviewed_at).toLocaleDateString()}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className="kyc-legal-save"
          onClick={handleSaveAllDocuments}
          disabled={Object.values(uploading).some(Boolean) || !Object.values(selectedFiles).some(Boolean)}
          style={{ marginTop: '1.25rem' }}
        >
          {Object.values(uploading).some(Boolean) ? 'Saving documents...' : 'Save documents'}
        </button>
        <p style={{ marginTop: '0.75rem', color: '#475569', fontSize: '0.9rem' }}>
          Attach each certificate above, then click <strong>Save documents</strong> to store them on your account.
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        background: '#e3f2fd',
        padding: '1rem',
        borderRadius: '8px',
        marginTop: '2rem',
        fontSize: '0.9rem',
        lineHeight: '1.6'
      }}>
        <strong>📋 Instructions:</strong><br/>
        1. Attach your IRA Licence, then click "Save documents"<br/>
        2. Credit Control can review after the IRA licence is uploaded<br/>
        <br/>
        <em>Note: Extra documents already on your account are kept. Certificate of Proficiency and incorporation certificates are not required. All documents must be clear and legible.</em>
      </div>
    </div>
  );
};

export default KYCSection;

